import { PlaybookType } from "@qw/qw-common";
import { Badge, Icon, type MaterialSymbol, Tooltip, TooltipContent, TooltipTrigger } from "@qw/ui";
import { type KeyboardEvent, memo, type ReactNode, useCallback } from "react";

const TYPE_BADGE: Record<PlaybookType, { icon: MaterialSymbol; tooltip: string }> = {
  [PlaybookType.Workflow]: { icon: "terminal", tooltip: "Instructional Workflow" },
  [PlaybookType.Knowledge]: { icon: "cognition", tooltip: "Contextual Knowledge" },
  [PlaybookType.Template]: { icon: "draft", tooltip: "Email Template" },
  [PlaybookType.WorkflowStep]: { icon: "linked_services", tooltip: "Workflow Step" },
};

export const PlaybookListItem = memo(function PlaybookListItem({
  title,
  description,
  author,
  subscribers,
  onClick,
  type,
  subscribed,
  isOwned,
}: {
  title: string;
  description?: string;
  author?: string;
  selected?: boolean;
  subscribers: Array<{ id: string; firstName?: string | null; lastName?: string | null }> | undefined;
  onClick: () => void;
  rightSlot?: ReactNode;
  type: PlaybookType;
  subscribed?: boolean;
  isOwned?: boolean;
}) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.code === "Enter" || event.code === "Space") {
        onClick();
      }
    },
    [onClick]
  );

  const badge = TYPE_BADGE[type];

  return (
    <div
      tabIndex={0}
      role="button"
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className="cursor-pointer p-4 bg-card hover:bg-background border rounded flex flex-col justify-between"
    >
      <div>
        <div className="font-medium text-wrap">
          <div className="mb-4 flex justify-between">
            <Tooltip>
              <TooltipTrigger asChild>
                <Icon icon={badge.icon} />
              </TooltipTrigger>
              <TooltipContent>{badge.tooltip}</TooltipContent>
            </Tooltip>
          </div>

          {title || "(untitled)"}
        </div>
        <div className="text-muted-foreground text-wrap text-sm ">{description ?? "No description"}</div>
      </div>

      <div className="mt-4 pt-2 border-t flex justify-between text-xs text-muted-foreground">
        <div>{author}</div>

        <div>
          <div className="flex gap-1">
            {isOwned && (
              <Badge variant="primary" size="xs" className="text-2xs gap-1">
                Owned
              </Badge>
            )}
            {subscribed && (
              <Badge variant="secondary" size="xs" className="text-2xs gap-1">
                Subscribed
              </Badge>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" size="xs" className="text-2xs gap-1">
                  <Icon icon="how_to_reg" className="text-xs" /> {subscribers?.length}
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {(!!subscribers?.length && (
                  <div className="flex flex-col gap-1 p-1">
                    <ul className="flex flex-col gap-1">
                      {subscribers?.map((s) => (
                        <li key={s.id}>
                          {s.firstName ?? ""} {s.lastName ?? ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                )) || <div>No subscribers</div>}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
});
